import { useState } from 'react';
import { Button } from '../../../shared/ui';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../../../shared/ui/shadcnSelect';
import { NAV_LINKS } from '../../../features/navigation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminPreferenceService } from '../service';
import { toast } from 'sonner';
import useProfile from '../hooks/useProfile';
import { useDispatch, useSelector } from 'react-redux';
import { setTotalItemPerpage } from '../../../redux/slices/authSlice';
import type { RootState } from '../../../redux/store';

const Preferences = () => {
  const [selectSignUpModule, setSelectSignUpModule] = useState("");
  const globalItemsPerPage = useSelector((state: RootState) => state.totalItemPerpage)
  const [itemsPerPage, setItemsPerPage] = useState<number>()
  const links = (Object.values(NAV_LINKS)).flat().filter(item => item.link !== '#')
  const pages = [20, 30, 50, 100]
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { profileData, refetchProfileData } = useProfile()

  const { mutateAsync: handleAdminPreference, isPending } = useMutation({
    mutationFn: () => adminPreferenceService({
      defaultModuleOnLogin: selectSignUpModule,
      totalItemPerpage: itemsPerPage,
    }),
    mutationKey: ['adminPreference']
  })

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      dispatch(setTotalItemPerpage(itemsPerPage))
      await handleAdminPreference()
      await refetchProfileData()
      toast.success("Preference updated succefully")
      queryClient.invalidateQueries({
        queryKey: ['activity-log']
      })
    } catch (error) {
      if (profileData?.totalItemPerpage) {
        dispatch(setTotalItemPerpage(profileData.totalItemPerpage))
        setItemsPerPage(profileData.totalItemPerpage)
      } else {
        setItemsPerPage(globalItemsPerPage || 20)
      }
    }
  }

  const isDisabled = !selectSignUpModule || !itemsPerPage || isPending

  return (
    <section className="space-y-7 rounded-lg md:w-[910px]">
      <div className="flex flex-col items-start gap-5 md:flex-row">
        <div className="md:w-4/12">
          <p className="text-lg font-[450] text-gray-30 leading-[-2.5%]">Display</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 w-full md:w-8/12 ">
          <div className="space-y-1">
            <p className="text-sm font-[450] text-gray-30">Default module upon sign in</p>
            <Select value={selectSignUpModule} onValueChange={setSelectSignUpModule}>
              <SelectTrigger className="newInput h-[56px] w-full md:w-[588px] !bg-primary-90">
                <SelectValue placeholder="Select module" />
              </SelectTrigger>
              <SelectContent className="border-none bg-white">
                <SelectGroup>
                  {links?.map((item) => {
                    return (
                      <SelectItem key={item.text} value={item.text}>
                        <p className='flex items-center gap-2'>
                          <span className='text-gray-30'>{item.icon(false)}</span> <span className='text-gray-30'>{item.text}</span>
                        </p>
                      </SelectItem>
                    )
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-[450] text-gray-30">Table items per page</p>
            <Select value={String(itemsPerPage)} onValueChange={(value) => setItemsPerPage(Number(value))}>
              <SelectTrigger className="newInput h-[56px] w-full md:w-[588px] !bg-primary-90">
                <SelectValue placeholder="20 rows per page" />
              </SelectTrigger>
              <SelectContent className="border-none bg-primary-90">
                <SelectGroup>
                 {pages.map((item) => {
                   return (
                     <SelectItem key={item} value={String(item)}>
                       <p className='flex items-center gap-2'>
                         <span className='text-gray-30'>{item}</span>
                       </p>
                     </SelectItem>
                   )
                 })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Save Changes Button */}
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={isDisabled}
            className="w-fit h-10 bg-primary-20 hover:bg-primary-30 text-base font-[450] rounded-[8px] px-4 py-2"
          >
            {isPending ? "Saving..." : "Save changes"}
          </Button>
        </form>
      </div>
    </section>
  );
}

export default Preferences
